import type { ExcalidrawElement } from "../../packages/excalidraw/element/types";

import type {
  MindmapCustomData,
  MindmapDeletionEntry,
} from "../../packages/mindmap-core/src/mindmapElements";
import {
  MINDMAP_DELETION_LEDGER_CAP,
  ledgerEntriesOf,
  withinDescendantBudget,
} from "../../packages/mindmap-core/src/mindmapElements";

/**
 * Union two peers' deletion ledgers before reconciliation picks a winner.
 *
 * Collab reconciles whole elements, last-writer-wins. The deletion ledger is a
 * LIST living on one element — the map root — so when two peers each touch the
 * root, one version wins and whatever the loser knew is gone for good: the
 * recorder only fires on a live→tombstoned transition, which has already
 * happened, so nothing ever writes it again. The next refresh then resurrects
 * that branch on BOTH peers.
 *
 * A ledger is a set of independent facts, so it merges cleanly where the element
 * around it cannot. Merging into both copies before the LWW comparison means
 * whichever side wins carries the union, and the reconciler itself stays
 * untouched.
 *
 * The loser does NOT have to be another deletion. A peer who merely dragged the
 * root carries no ledger at all, and letting that win wipes the other peer's —
 * which is why a missing side is treated as an empty ledger rather than as
 * "nothing to merge". Legacy roots holding only `deletedBlockIds` are read
 * through the same migration the rest of the code uses, so they are not
 * invisible here either.
 *
 * This also covers the recorder having no local/remote discriminator: a peer
 * applying someone else's deletion records it as its own, which is the same
 * entry by blockId and folds into the same union.
 */

const mindmapOf = (element: ExcalidrawElement): MindmapCustomData | undefined =>
  element.customData?.mindmap as MindmapCustomData | undefined;

/** Newest entry per blockId, oldest dropped once past the cap. */
const unionLedgers = (
  a: readonly MindmapDeletionEntry[],
  b: readonly MindmapDeletionEntry[],
): MindmapDeletionEntry[] => {
  const byBlock = new Map<string, MindmapDeletionEntry>();
  for (const entry of [...a, ...b]) {
    const existing = byBlock.get(entry.blockId);
    if (!existing || entry.at > existing.at) {
      byBlock.set(entry.blockId, entry);
    }
  }
  // Total order, not just by `at`: every legacy-migrated entry carries at: 0, so
  // ties are the norm rather than the exception, and two peers sorting them
  // differently would re-version the root on every single sync, forever.
  // Both budgets, not just the entry count: a union of two peers' ledgers can
  // carry more remembered descendants than either side alone, and this result
  // is what then rides every autosave and broadcast.
  return withinDescendantBudget(
    [...byBlock.values()]
      .sort((x, y) => x.at - y.at || (x.blockId < y.blockId ? -1 : 1))
      .slice(-MINDMAP_DELETION_LEDGER_CAP),
  );
};

/** Same facts, whatever order they are in. */
const sameLedger = (
  a: readonly MindmapDeletionEntry[],
  b: readonly MindmapDeletionEntry[],
): boolean => {
  if (a.length !== b.length) {
    return false;
  }
  const byBlock = new Map(a.map((entry) => [entry.blockId, entry.at]));
  return b.every((entry) => byBlock.get(entry.blockId) === entry.at);
};

/**
 * The element with a merged ledger, built by spread rather than
 * `newElementWith`: that bumps `version` and re-randomises `versionNonce`, and
 * each peer would stamp its own — so at equal versions the nonce tie-break could
 * settle on different copies of the root on different peers, and position,
 * label, expanded state and colour would diverge permanently. Both sides now
 * hold identical ledger content, so there is nothing to win.
 */
const withLedger = (
  element: ExcalidrawElement,
  deletions: MindmapDeletionEntry[],
): ExcalidrawElement => ({
  ...element,
  customData: {
    ...element.customData,
    mindmap: { ...mindmapOf(element), deletions },
  },
});

export const withMergedMindmapLedgers = (
  localElements: readonly ExcalidrawElement[],
  remoteElements: readonly ExcalidrawElement[],
  /**
   * Local elements the user is actively editing. Replacing one mid-gesture
   * swaps the object `appState.draggingElement` points at, so they are left
   * alone — the next sync merges them once the gesture ends.
   */
  busyLocalIds: ReadonlySet<string> = new Set(),
): {
  local: readonly ExcalidrawElement[];
  remote: readonly ExcalidrawElement[];
} => {
  // Fast path for the overwhelming majority of syncs: a delta with no mindmap
  // element in it cannot need a ledger merge, and this runs on every remote
  // update.
  const remoteMindmap = remoteElements.filter((el) => mindmapOf(el));
  if (remoteMindmap.length === 0) {
    return { local: localElements, remote: remoteElements };
  }

  const localById = new Map<string, ExcalidrawElement>();
  for (const element of localElements) {
    if (mindmapOf(element)) {
      localById.set(element.id, element);
    }
  }

  const merged = new Map<string, MindmapDeletionEntry[]>();
  for (const remote of remoteMindmap) {
    const local = localById.get(remote.id);
    if (!local || busyLocalIds.has(remote.id)) {
      continue;
    }
    const remoteLedger = ledgerEntriesOf(mindmapOf(remote));
    const localLedger = ledgerEntriesOf(mindmapOf(local));
    if (remoteLedger.length === 0 && localLedger.length === 0) {
      continue;
    }
    merged.set(remote.id, unionLedgers(localLedger, remoteLedger));
  }
  if (merged.size === 0) {
    return { local: localElements, remote: remoteElements };
  }

  const apply = (
    elements: readonly ExcalidrawElement[],
  ): readonly ExcalidrawElement[] =>
    elements.map((element) => {
      const union = merged.get(element.id);
      return union && !sameLedger(ledgerEntriesOf(mindmapOf(element)), union)
        ? withLedger(element, union)
        : element;
    });

  return { local: apply(localElements), remote: apply(remoteElements) };
};
